import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSelectedMover, updateSelectedMover } from "@/store/mover/slice";
import { useUpdateMoverMutation } from "@/store/mover/apis";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { LaborRate } from "@/types";
import {
  Table,
  TableBody,
  TableHeader,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Label } from "@/components/ui/label";
import { Save } from "lucide-react";

const LaborRates: React.FC = () => {
  const dispatch = useDispatch();
  const selectedMover = useSelector(getSelectedMover);
  const { toast } = useToast();
  const [updateMoverApi, { isLoading }] = useUpdateMoverMutation();

  // Initialize labor rates from mover data or with default values
  const [laborRates, setLaborRates] = useState<LaborRate[]>(() => {
    return [1, 2, 3, 4, 5, 6, 0].map((crewSize) => ({
      id:
        selectedMover?.labor_rates.find((rate) => rate.crew_size === crewSize)
          ?.id || 0, // Will be replaced on save if new
      mover_id: selectedMover?.id || 0,
      crew_size: crewSize,
      mon_thu_rate:
        selectedMover?.labor_rates.find((rate) => rate.crew_size === crewSize)
          ?.mon_thu_rate || 0,
      fri_rate:
        selectedMover?.labor_rates.find((rate) => rate.crew_size === crewSize)
          ?.fri_rate || 0,
      sat_rate:
        selectedMover?.labor_rates.find((rate) => rate.crew_size === crewSize)
          ?.sat_rate || 0,
      sun_rate:
        selectedMover?.labor_rates.find((rate) => rate.crew_size === crewSize)
          ?.sun_rate || 0,
      nature:
        selectedMover?.labor_rates.find((rate) => rate.crew_size === crewSize)
          ?.nature || "per_hour",
      active:
        selectedMover?.labor_rates.find((rate) => rate.crew_size === crewSize)
          ?.active || true,
      description:
        selectedMover?.labor_rates.find((rate) => rate.crew_size === crewSize)
          ?.description ||
        (crewSize === 0
          ? "Additional Mover"
          : `${crewSize} Mover${crewSize > 1 ? "s" : ""}`),
      created_at:
        selectedMover?.labor_rates.find((rate) => rate.crew_size === crewSize)
          ?.created_at || "",
      updated_at:
        selectedMover?.labor_rates.find((rate) => rate.crew_size === crewSize)
          ?.updated_at || "",
    }));
  });

  const handleRateChange = (
    index: number,
    field: keyof LaborRate,
    value: string
  ) => {
    const newRates = [...laborRates];
    // Convert to number and handle empty strings or negative values
    let numValue = value === "" ? 0 : parseFloat(value);
    numValue = Math.max(0, numValue); // Ensure value is at least 0

    newRates[index] = {
      ...newRates[index],
      [field]: numValue,
    };
    setLaborRates(newRates);
  };

  const handleSave = async () => {
    if (!selectedMover) return;

    if (
      laborRates.some((rate) => !rate.mon_thu_rate || rate.mon_thu_rate <= 0)
    ) {
      toast({
        title: "Error",
        description: "Rate cannot be empty or 0",
        variant: "destructive",
      });

      return;
    }

    if (laborRates.some((rate) => !rate.fri_rate || rate.fri_rate <= 0)) {
      toast({
        title: "Error",
        description: "Rate cannot be empty or 0",
        variant: "destructive",
      });

      return;
    }

    if (laborRates.some((rate) => !rate.sat_rate || rate.sat_rate <= 0)) {
      toast({
        title: "Error",
        description: "Rate cannot be empty or 0",
        variant: "destructive",
      });

      return;
    }

    if (laborRates.some((rate) => !rate.sun_rate || rate.sun_rate <= 0)) {
      toast({
        title: "Error",
        description: "Rate cannot be empty or 0",
        variant: "destructive",
      });

      return;
    }

    try {
      const updatedMover = await updateMoverApi({
        data: {
          labor_rates_attributes: laborRates.map((rate) => ({
            id: rate.id || undefined, // Only include ID if it exists (not 0)
            mover_id: selectedMover.id,
            crew_size: rate.crew_size,
            mon_thu_rate: rate.mon_thu_rate,
            fri_rate: rate.fri_rate,
            sat_rate: rate.sat_rate,
            sun_rate: rate.sun_rate,
            nature: rate.nature,
            active: rate.active,
            description: rate.description,
          })),
        },
      }).unwrap();

      dispatch(updateSelectedMover(updatedMover));

      toast({
        title: "Success",
        description: "Labor rates updated successfully.",
      });
    } catch (error) {
      console.error("Error updating labor rates:", error);
      toast({
        title: "Error",
        description: "Failed to update labor rates. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getRowLabel = (crewSize: number): string => {
    if (crewSize === 0) return "Additional Mover";
    return `${crewSize} Mover${crewSize > 1 ? "s" : ""}`;
  };

  if (!selectedMover) return null;

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold">Labor Rates</h2>
      <p className="text-base text-gray-800 mt-1 mb-6">
        Set your standard hourly labor rates{" "}
        <span className="font-medium">
          (you can override these on a per move basis).
        </span>
      </p>

      <div className="block md:hidden">
        {/* Mobile view - stacked layout */}
        {laborRates.map((rate, index) => (
          <div key={index} className="mb-6 border-b pb-4">
            <h3 className="font-medium mb-2">{getRowLabel(rate.crew_size)}</h3>

            <div className="mb-3">
              <Label className="block text-sm font-normal mb-1">
                Monday to Thursday
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-900">
                  $
                </span>
                <Input
                  type="number"
                  min="0"
                  value={rate.mon_thu_rate || ""}
                  onChange={(e) =>
                    handleRateChange(index, "mon_thu_rate", e.target.value)
                  }
                  onFocus={(e) => e.target.select()}
                  className="pl-6 pr-24 w-full"
                  required
                />
                <button
                  type="button"
                  onClick={() => {
                    const newRates = [...laborRates];
                    newRates[index] = {
                      ...newRates[index],
                      fri_rate: rate.mon_thu_rate,
                      sat_rate: rate.mon_thu_rate,
                      sun_rate: rate.mon_thu_rate,
                    };
                    setLaborRates(newRates);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-sky-600 hover:text-sky-800 font-semibold"
                >
                  Copy to Fri-Sun
                </button>
              </div>
            </div>

            <div className="mb-3">
              <Label className="block text-sm font-normal mb-1">Friday</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-900">
                  $
                </span>
                <Input
                  type="number"
                  min="0"
                  value={rate.fri_rate || ""}
                  onChange={(e) =>
                    handleRateChange(index, "fri_rate", e.target.value)
                  }
                  onFocus={(e) => e.target.select()}
                  className="pl-6 pr-24 w-full"
                  required
                />
                <button
                  type="button"
                  onClick={() => {
                    const newRates = [...laborRates];
                    newRates[index] = {
                      ...newRates[index],
                      sat_rate: rate.fri_rate,
                      sun_rate: rate.fri_rate,
                    };
                    setLaborRates(newRates);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-sky-600 hover:text-sky-800 font-semibold"
                >
                  Copy to Sat-Sun
                </button>
              </div>
            </div>

            <div className="mb-3">
              <Label className="block text-sm font-normal mb-1">Saturday</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-900">
                  $
                </span>
                <Input
                  type="number"
                  min="0"
                  value={rate.sat_rate || ""}
                  onChange={(e) =>
                    handleRateChange(index, "sat_rate", e.target.value)
                  }
                  onFocus={(e) => e.target.select()}
                  className="pl-6 pr-16 w-full"
                  required
                />
                <button
                  type="button"
                  onClick={() => {
                    const newRates = [...laborRates];
                    newRates[index] = {
                      ...newRates[index],
                      sun_rate: rate.sat_rate,
                    };
                    setLaborRates(newRates);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-sky-600 hover:text-sky-800 font-semibold"
                >
                  Copy to Sun
                </button>
              </div>
            </div>

            <div className="mb-3">
              <Label className="block text-sm font-normal mb-1">Sunday</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-900">
                  $
                </span>
                <Input
                  type="number"
                  min="0"
                  value={rate.sun_rate || ""}
                  onChange={(e) =>
                    handleRateChange(index, "sun_rate", e.target.value)
                  }
                  onFocus={(e) => e.target.select()}
                  className="pl-6 w-full"
                  required
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="hidden md:block overflow-x-auto">
        <Table className="w-full border-collapse">
          <TableHeader>
            <TableRow>
              <TableHead></TableHead>
              <TableHead className="font-normal text-black">
                Monday to Thursday
              </TableHead>
              <TableHead className="font-normal text-black">Friday</TableHead>
              <TableHead className="font-normal text-black">Saturday</TableHead>
              <TableHead className="font-normal text-black">Sunday</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {laborRates.map((rate, index) => (
              <TableRow key={index} className="">
                <TableCell className="px-2 py-3">
                  {getRowLabel(rate.crew_size)}
                </TableCell>
                <TableCell className="px-2 py-3">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-900">
                      $
                    </span>
                    <Input
                      type="number"
                      min="0"
                      value={rate.mon_thu_rate || ""}
                      onChange={(e) =>
                        handleRateChange(index, "mon_thu_rate", e.target.value)
                      }
                      onFocus={(e) => e.target.select()}
                      className="pl-6 pr-24 w-full"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newRates = [...laborRates];
                        newRates[index] = {
                          ...newRates[index],
                          fri_rate: rate.mon_thu_rate,
                          sat_rate: rate.mon_thu_rate,
                          sun_rate: rate.mon_thu_rate,
                        };
                        setLaborRates(newRates);
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-sky-600 hover:text-sky-800 font-semibold"
                    >
                      Copy to Fri-Sun
                    </button>
                  </div>
                </TableCell>
                <TableCell className="px-2 py-3">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-900">
                      $
                    </span>
                    <Input
                      type="number"
                      min="0"
                      value={rate.fri_rate || ""}
                      onChange={(e) =>
                        handleRateChange(index, "fri_rate", e.target.value)
                      }
                      onFocus={(e) => e.target.select()}
                      className="pl-6 pr-24 w-full"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newRates = [...laborRates];
                        newRates[index] = {
                          ...newRates[index],
                          sat_rate: rate.fri_rate,
                          sun_rate: rate.fri_rate,
                        };
                        setLaborRates(newRates);
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-sky-600 hover:text-sky-800 font-semibold"
                    >
                      Copy to Sat-Sun
                    </button>
                  </div>
                </TableCell>
                <TableCell className="p-2">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-900">
                      $
                    </span>
                    <Input
                      type="number"
                      min="0"
                      value={rate.sat_rate || ""}
                      onChange={(e) =>
                        handleRateChange(index, "sat_rate", e.target.value)
                      }
                      onFocus={(e) => e.target.select()}
                      className="pl-6 pr-16 w-full"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newRates = [...laborRates];
                        newRates[index] = {
                          ...newRates[index],
                          sun_rate: rate.sat_rate,
                        };
                        setLaborRates(newRates);
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-sky-600 hover:text-sky-800 font-semibold"
                    >
                      Copy to Sun
                    </button>
                  </div>
                </TableCell>
                <TableCell className="p-2">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-900">
                      $
                    </span>
                    <Input
                      type="number"
                      min="0"
                      value={rate.sun_rate || ""}
                      onChange={(e) =>
                        handleRateChange(index, "sun_rate", e.target.value)
                      }
                      onFocus={(e) => e.target.select()}
                      className="pl-6 w-full"
                      required
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="bg-stone-50 fixed bottom-0 left-0 w-full p-4 border-t border-gray-200">
        <Button
          type="button"
          onClick={handleSave}
          disabled={isLoading}
          size="lg"
        >
          <Save className="h-4 w-4 mr-2" />
          {isLoading ? "Saving..." : "Save Rates"}
        </Button>
      </div>
    </div>
  );
};

export default LaborRates;
